import {ArrowNarrowLeftIcon} from '@heroicons/react/solid';
import type {JSX} from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';

import {buildDownloadFileName} from '@afriex/core/utils';

import Spinner from '@/assets/svg/Spinner';
import {Header} from '@/components';
import type {PaginationProps} from '@/components/common/Pagination';
import BusinessTransactionHistory, {
  BUSINESS_HISTORY_PAGE_SIZE,
} from '@/components/transactions/BusinessHistory';
import BusinessTransactionDetail from '@/screens/Merchants/Transactions/List/TransactionDetail';
import {merchantTransactionService} from '@/services/merchants/transactionService';
import type {AfriexBusinessTransaction} from '@/types/AfriexBusinessTransaction';
import {getBusinessTxHistoryScope} from '@/utils/businessTransactionFormatter';
import {formatBusinessAdminTransaction} from '@/utils/dashboard';
import {saveBlob} from '@/utils/downloadFile';
import {getErrorMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';
import {showErrorMessage} from '@/utils/showErrorMessage';

export default function MerchantTransactionDetailsPage(): JSX.Element {
  const navigate = useNavigate();
  const params = useParams();
  const [transactionHistory, setTransactionHistory] = useState<
    AfriexBusinessTransaction[]
  >([]);
  const [transactionDetail, setTransactionDetail] =
    useState<AfriexBusinessTransaction>({} as AfriexBusinessTransaction);

  const [loadingTnxDetail, setLoadingTnxDetail] = useState<boolean>(false);
  const [loadingTnxHistory, setLoadingTnxHistory] = useState<boolean>(false);
  // Whose history the page shows, the customer's or (for a transaction the
  // business's staff started from the dashboard) the business's; both the
  // history pages and the export are scoped to it.
  const [historyScope, setHistoryScope] = useState<
    {userId: string} | {businessId: string} | undefined
  >();
  const [totalTransactions, setTotalTransactions] = useState<number>(0);
  const [loadingExport, setLoadingExport] = useState<boolean>(false);
  // 0-indexed page the Recent History table is showing. Tracked so the page
  // handler can ignore the `onPageChange({page: 0})` the Pagination footer
  // fires on mount, and handed to the footer so its page number follows a
  // reload it did not start.
  const [historyPage, setHistoryPage] = useState<number>(0);
  // Only the latest history request may write state: an older page (or the
  // previous transaction's history) that resolves late must not replace it.
  const historyFetchIdRef = useRef<number>(0);

  // Exports the same full history the Recent History panel lists, as a
  // server-streamed CSV. It used to export a prefetched first 100
  // transactions across every business instead.
  async function exportTransactionHistory(): Promise<void> {
    if (!historyScope) {
      return;
    }
    setLoadingExport(true);
    try {
      const blob =
        await merchantTransactionService.downloadBusinessTransactionList(
          historyScope,
        );
      const scope =
        'businessId' in historyScope
          ? historyScope.businessId
          : historyScope.userId;
      saveBlob(
        blob,
        buildDownloadFileName('business_transactions', 'csv', scope),
      );
    } catch (error) {
      showErrorMessage(getErrorMessage(error, 'Failed to export transactions'));
    } finally {
      setLoadingExport(false);
    }
  }

  function onExport(): void {
    // exportTransactionHistory reports its own failures, so this only fires
    // if that reporting itself throws.
    exportTransactionHistory().catch(
      logRejection('Failed to export transactions'),
    );
  }

  const fetchTransactionHistory = useCallback(
    async (
      scope: {userId: string} | {businessId: string},
      page = 0,
    ): Promise<void> => {
      const fetchId = ++historyFetchIdRef.current;
      try {
        setLoadingTnxHistory(true);
        const response =
          await merchantTransactionService.getBusinessTransactionList({
            ...scope,
            page,
            limit: BUSINESS_HISTORY_PAGE_SIZE,
          });
        if (fetchId !== historyFetchIdRef.current) {
          return;
        }
        setTransactionHistory(response.data);
        setTotalTransactions(response.total);
        setHistoryPage(page);
        setLoadingTnxHistory(false);
      } catch (_err) {
        if (fetchId === historyFetchIdRef.current) {
          setLoadingTnxHistory(false);
        }
      }
    },
    [],
  );

  /**
   * Load the page the footer asked for.
   *
   * `Pagination` calls `onPageChange` once on mount with page 0 as its own
   * state settles. The detail load already fetched page 0, so an unguarded
   * handler would refetch it on every open. Only a page that actually changed
   * hits the API, and only once the history scope is known.
   */
  const handleHistoryPageChange = useCallback(
    (pageParams: {limit: number; page: number}): Promise<void> => {
      if (pageParams.page === historyPage || !historyScope) {
        return Promise.resolve();
      }
      return fetchTransactionHistory(historyScope, pageParams.page);
    },
    [fetchTransactionHistory, historyPage, historyScope],
  );

  const fetchTransactionDetails = useCallback(async () => {
    try {
      setLoadingTnxDetail(true);
      const currentTransaction =
        await merchantTransactionService.getBusinessTransaction({
          id: params.transactionId as string,
        });
      const filtered = await formatBusinessAdminTransaction(currentTransaction);
      setTransactionDetail({...currentTransaction, ...filtered});
      // Undefined when the id is missing, which would otherwise be serialized
      // as the literal `userId=undefined` and rejected by the server.
      const scope = getBusinessTxHistoryScope(currentTransaction);
      setHistoryScope(scope);
      if (scope) {
        // Deliberately not awaited, as today: the detail view finishes loading
        // without waiting for the history list. `fetchTransactionHistory`
        // catches its own failures, so this only fires if that catch throws.
        fetchTransactionHistory(scope).catch(
          logRejection('Failed to load transaction history'),
        );
      }
      setLoadingTnxDetail(false);
    } catch (_err) {
      setLoadingTnxDetail(false);
    }
  }, [params.transactionId, fetchTransactionHistory]);

  useEffect(() => {
    // `fetchTransactionDetails` catches its own failures, so this only fires if
    // that catch itself throws. Log rather than swallow silently.
    fetchTransactionDetails().catch(
      logRejection('Failed to load business transaction details'),
    );
  }, [fetchTransactionDetails]);

  return (
    <div className="mt-10 mb-5 mx-4 md:mx-10">
      <Header
        title="Business Transaction Details"
        dataCy="merchant-transaction-details-heading"
        icon={
          <ArrowNarrowLeftIcon
            className="font-light w-5 h-5 text-black dark:text-white hover:text-indigo-500 dark:hover:text-indigo-500 cursor-pointer"
            onClick={() => navigate(-1)}
            data-cy="merchant-transaction-details-back"
          />
        }
      />

      <div className="flex justify-center items-center flex-col">
        {loadingTnxDetail ? (
          <Spinner size={8} className="text-cyan-600" />
        ) : (
          <div
            className="mt-10 w-10/12 rounded-md bg-white dark:bg-gray-900 border border-gray-300 p-7 flex flex-col dark:border-dark-borderHeavy"
            data-cy="merchant-transaction-details"
          >
            {transactionDetail && Object.keys(transactionDetail).length > 0 && (
              <BusinessTransactionDetail
                data={transactionDetail}
                recentHistory={
                  // Always mounted, with the table owning its loading state:
                  // keeping it (and its pagination footer) mounted across page
                  // fetches stops `Pagination` remounting, whose mount effect
                  // fires `onPageChange({page: 0})` and would snap the view
                  // back to the first page.
                  <BusinessTransactionHistory
                    data={transactionHistory}
                    handleExport={onExport}
                    loadingExport={loadingExport}
                    totalTransactions={totalTransactions}
                    isLoading={loadingTnxHistory}
                    page={historyPage}
                    title={
                      historyScope && 'businessId' in historyScope
                        ? 'Recent Business History'
                        : 'Recent History'
                    }
                    onPageChange={
                      handleHistoryPageChange as PaginationProps['onPageChange']
                    }
                  />
                }
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
